import _ from 'lodash';
import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';
import Countdown from 'react-countdown-simple';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  KeyValEntry,
  readAllStoredKeyValuesForOrgaAndPhase
} from '../../../Helper/ApiHelper/KeyValueNetworkHelper';
import {
  EP,
  readEpCatalogForPhase
} from '../../../Helper/ApiHelper/LvEpNetworkHelper';
import { DURATION_NOTIFICATION_ERROR_LONG } from '../../../Helper/Statics/Constants';
import {
  extractUserId,
  hasUserWriteRights
} from '../../../Helper/Util/JwtHelper';
import {
  checkStepActivation,
  checkUserParticipation,
  STEP_IS_ACTIVE,
  STEP_WILL_BE_ACTIVE
} from '../../../Helper/Util/TimelineStepHelper';
import { AppDispatch, RootState } from '../../../Redux/store';
import BeeContentHeadline from '../../Atoms/BeeContentHeadline';
import BeeLoadingSpinner from '../../Atoms/BeeLoadingSpinner';
import BeeTimeline, { TimelineStep } from '../../Atoms/BeeTimeline';
import DLRahmenpreiseingabe from '../../Organisms/DL/DLRahmenpreiseingabe';
import './DLPriceInputPage.scss';
import { isKeyUserEpMapKey } from '../../../Helper/Util/IdGeneratorHelper';

export type DocType = {
  id: string;
  title: string;
  filename?: string;
  fileSrc: string;
  timestamp: Date;
  size?: number;
  type: string;
  fileKey?: string;
  copyright: string;
  secured: boolean;
};

type DLPriceInputPageProps = {
  dlPhasen: TimelineStep[] | undefined;
};

export type FileUploadType = {
  file: File | null | undefined;
  name: string | null;
  copyright: string | null;
  originalFileName: string | null | undefined;
};

export function DLPriceInputPage(props: DLPriceInputPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [phasen, setPhasen] = useState<TimelineStep[] | undefined>(undefined);
  const [currentEpCatalog, setCurrentEpCatalog] = useState<EP>();
  const [selectedPhase, setSelectedPhase] = useState<TimelineStep>();
  const [readOnly, setReadOnly] = useState<boolean>(false);
  const [fatalError, setFatalError] = useState<boolean>(false);
  const [hasNoPhases, setHasNoPhases] = useState<boolean>(false);
  const [isPhaseDataLoading, setIsPhaseDataLoading] = useState<boolean>(false);
  const [initialUserInputs, setInitialUserInputs] = useState<KeyValEntry[]>();
  const toast = useRef<Toast>(null);
  const phaseDataRequestIdRef = useRef(0);
  const isPhasesLoading = props.dlPhasen == null;

  useEffect(() => {
    if (props.dlPhasen == null) {
      return;
    }
    if (props.dlPhasen.length === 0) {
      setHasNoPhases(true);
      setPhasen([]);
      setSelectedPhase(undefined);
      setInitialUserInputs(undefined);
      setCurrentEpCatalog(undefined);
      setIsPhaseDataLoading(false);
      return;
    }
    setHasNoPhases(false);
    if (!phasen) {
      setInitialUserInputs(undefined);
      setCurrentEpCatalog(undefined);
    }
    const steps: TimelineStep[] = [...props.dlPhasen];
    if (!_.isEqual(phasen, steps) || searchParams.get('pId') === null) {
      setInitialUserInputs(undefined);
      setCurrentEpCatalog(undefined);
      setPhasen(steps);
      let activePhase: TimelineStep | undefined;
      let stepFound = false;
      for (let i = 0; i < steps.length; i++) {
        if (checkStepActivation(steps[i]) === STEP_IS_ACTIVE) {
          activePhase = steps[i];
          setSelectedPhase(activePhase);
          if (checkUserParticipation(activePhase)) {
            stepFound = true;
            if (searchParams.get('pId') !== activePhase.id) {
              const nextParams = new URLSearchParams(searchParams);
              nextParams.set('pId', activePhase.id);
              setSearchParams(nextParams);
            }
          } else {
            while (i > 0) {
              if (checkUserParticipation(steps[i])) {
                stepFound = true;
                if (searchParams.get('pId') !== steps[i].id) {
                  const nextParams = new URLSearchParams(searchParams);
                  nextParams.set('pId', steps[i].id);
                  setSearchParams(nextParams);
                  break;
                }
              }
              i = i - 1;
            }
            //if user is in no phase he will be shown the first one
          }
          break;
        }
      }
      if (!stepFound) {
        /* fixme dl: wollen wir immer den ersten step zeigen auch wenn wir nicht teil davon sind */
        if (steps.length > 0) {
          if (searchParams.get('pId') !== steps[0]?.id) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('pId', steps[0].id);
            setSearchParams(nextParams);
          }
          setSelectedPhase(steps[0]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dlPhasen, searchParams]);

  useEffect(() => {
    if (!phasen || phasen.length === 0) {
      return;
    }
    const phaseId = searchParams.get('pId');
    if (!phaseId) {
      setInitialUserInputs(undefined);
      setCurrentEpCatalog(undefined);
      setIsPhaseDataLoading(false);
      return;
    }
    const currPhase = _.find(phasen, { id: phaseId });
    if (!currPhase) {
      const fallback = phasen[0];
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('pId', fallback.id);
      setSearchParams(nextParams);
      return;
    }
    const readOnlyValue =
      checkStepActivation(currPhase) !== STEP_IS_ACTIVE ||
      !checkUserParticipation(currPhase) ||
      !hasUserWriteRights();
    setReadOnly(readOnlyValue);
    const shouldRefreshPhase =
      !selectedPhase ||
      selectedPhase.id !== currPhase.id ||
      (!initialUserInputs && !fatalError && !isPhaseDataLoading);
    setSelectedPhase(currPhase);
    if (shouldRefreshPhase) {
      loadPhaseData(currPhase);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, phasen]);

  function loadPhaseData(phase: TimelineStep) {
    const requestId = ++phaseDataRequestIdRef.current;
    setFatalError(false);
    setIsPhaseDataLoading(true);
    setInitialUserInputs(undefined);
    setCurrentEpCatalog(undefined);
    const userId = extractUserId() || 'no_user';
    Promise.all([
      readAllStoredKeyValuesForOrgaAndPhase(userId, phase.id),
      readEpCatalogForPhase(phase.id)
    ])
      .then(([data, ep]) => {
        if (requestId !== phaseDataRequestIdRef.current) {
          return;
        }
        const catalog: EP = ep as EP;
        const keyValues = [...(data as KeyValEntry[])];
        const selectedEpCodes = extractSelectedEpCodesFromKeyValues(
          keyValues,
          phase.id
        );
        const reducedCatalog = reduceEpCatalogByEpCodes(
          catalog,
          selectedEpCodes
        );
        setCurrentEpCatalog(reducedCatalog);
        setInitialUserInputs(keyValues);
      })
      .catch(() => {
        if (requestId !== phaseDataRequestIdRef.current) {
          return;
        }
        setFatalError(true);
        toast.current?.show({
          severity: 'error',
          summary: 'Daten konnten nicht geladen werden.',
          detail:
            'Das Laden des EP-Katalogs und Ihrer eingegebenen Preise und Faktoren ist schiefgelaufen. Bitte aktualisieren Sie die Seite. Sollte das Problem weiterhin bestehen wenden Sie sich bitte an den Kundenservice.',
          sticky: false,
          closable: true,
          life: DURATION_NOTIFICATION_ERROR_LONG
        });
      })
      .finally(() => {
        if (requestId === phaseDataRequestIdRef.current) {
          setIsPhaseDataLoading(false);
        }
      });
  }

  function extractSelectedEpCodesFromKeyValues(
    keyValues: KeyValEntry[],
    phaseId: string
  ): Set<string> {
    const prefix = 'user_ep_map_' + phaseId + '_';
    const selectedCodes = new Set<string>();
    keyValues.forEach((entry) => {
      if (
        entry.key &&
        entry.key.startsWith(prefix) &&
        isKeyUserEpMapKey(entry.key) &&
        isTrueKeyValue(entry.value)
      ) {
        const epCode = entry.key.substring(prefix.length);
        if (epCode) {
          selectedCodes.add(epCode);
        }
      }
    });
    return selectedCodes;
  }

  function reduceEpCatalogByEpCodes(
    catalog: EP,
    selectedEpCodes: Set<string>
  ): EP {
    if (!catalog || !catalog.data) {
      return catalog;
    }

    const filteredData =
      selectedEpCodes.size > 0
        ? catalog.data.filter((entry) => {
            const isLeaf = entry.leftInt + 1 === entry.rightInt;
            return (
              !isLeaf || (entry.epCode && selectedEpCodes.has(entry.epCode))
            );
          })
        : [];

    return { ...catalog, data: filteredData };
  }

  function isTrueKeyValue(value: any): boolean {
    if (value === true) {
      return true;
    }
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return false;
  }

  function onReloadKeyValues() {
    if (selectedPhase) {
      loadPhaseData(selectedPhase);
    }
  }

  ///////////////////////
  /// VIEW COMPONENTS ///
  ///////////////////////

  function injectNoPhasesMessage(): JSX.Element {
    return (
      <div className={'priceInput-fatal-error-msg'}>
        <i className="pi pi-exclamation-triangle mr-2 error"></i>
        <h1 className="error">Keine Phasen vorhanden</h1>
        <div>
          <span className="error">
            Aktuell wurden von der Administration noch keine Projektphasen
            hinterlegt. Bitte versuchen Sie es zu einem späteren Zeitpunkt
            erneut.
          </span>
        </div>
      </div>
    );
  }

  function injectPageSpinner(): JSX.Element {
    return (
      <span className={'loader-page'}>
        <BeeLoadingSpinner strokeWidth={'3'} type={'primary'} />
      </span>
    );
  }

  function injectHeadline(): JSX.Element {
    return (
      <div className={'priceInput-header'}>
        <BeeContentHeadline
          label={'Rahmenpreiseingabe'}
          headline={'h1'}
          type={'primary'}
        />
        {selectedPhase ? (
          <span className={'phase-countdown '}>
            <span className={'bold'}>{'Phase endet in:'}</span>
            <br />
            {renderCountdown(selectedPhase)}
          </span>
        ) : null}
      </div>
    );
  }

  function renderCountdown(item: TimelineStep): JSX.Element {
    const activation: string = checkStepActivation(item);
    if (activation === STEP_WILL_BE_ACTIVE) {
      return <div></div>;
    } else {
      /* is active */
      if (activation === STEP_IS_ACTIVE) {
        return <Countdown targetDate={item.end} renderer={countDownRenderer} />;
      } else {
        /* abgelaufen */
        return <div>{'Phase beendet'}</div>;
      }
    }
  }

  const countDownRenderer = ({
    days,
    hours,
    minutes,
    seconds
  }: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }) => {
    return (
      <div className={'countdown'}>
        <div className={'countdownBlock'}>
          <div className={'countdownNumber'}>{days}</div>
          <div className={'countdownLabel'}>Tage</div>{' '}
        </div>
        <div className={'countdownBlock'}>
          <div className={'countdownNumber'}>{hours}</div>
          <div className={'countdownLabel'}>Std</div>
        </div>
        <div className={'countdownBlock'}>
          <div className={'countdownNumber'}>{minutes}</div>
          <div className={'countdownLabel'}>Min</div>
        </div>
        <div className={'countdownBlock'}>
          <div className={'countdownNumber'}>{seconds}</div>
          <div className={'countdownLabel'}>Sek</div>
        </div>
        <br />
      </div>
    );
  };

  function injectTimelineSection(): JSX.Element {
    if (hasNoPhases) {
      return injectNoPhasesMessage();
    }
    if (isPhasesLoading) {
      return injectPageSpinner();
    }
    if (phasen && selectedPhase) {
      return <BeeTimeline steps={phasen} currentPhase={selectedPhase} />;
    }
    return injectPageSpinner();
  }

  function injectContentSection(): JSX.Element {
    if (hasNoPhases) {
      return <></>;
    }
    if (isPhasesLoading) {
      return injectPageSpinner();
    }
    if (fatalError) {
      return (
        <div className={'priceInput-fatal-error-msg'}>
          <i className="pi pi-exclamation-triangle mr-2 error"></i>
          <h1 className="error">Fehler</h1>
          <div>
            <span className="error">
              Der EP-Katalog und Ihre bisher eigegebenen Daten konnten nicht vom
              Server geladen werden.
            </span>{' '}
            <span className="error">
              Bitte aktualisieren Sie die Seite. Sollte das Problem weiterhin
              bestehen wenden Sie sich bitte an den Kundenservice.
            </span>
          </div>
        </div>
      );
    }
    if (!selectedPhase) {
      return injectPageSpinner();
    }
    if (isPhaseDataLoading || initialUserInputs === undefined) {
      return injectPageSpinner();
    }
    return (
      <DLRahmenpreiseingabe
        readOnly={readOnly}
        phase={selectedPhase}
        keyValues={initialUserInputs}
        epCatalog={currentEpCatalog}
        onReloadKeyValues={onReloadKeyValues}
      />
    );
  }

  return (
    <>
      <div className={'dl-priceInput-page'}>
        {injectHeadline()}
        {injectTimelineSection()}
        <div>{injectContentSection()}</div>
      </div>
      <Toast ref={toast} position={'top-right'} />
    </>
  );
}

const mapState = (state: RootState) => {
  return {
    dlPhasen: state.app.dlPhasen
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {};
};

export default connect(mapState, mapDispatchToProps)(DLPriceInputPage);
